import { Component, inject, OnInit, Input, ElementRef, HostListener, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Firestore, collection, collectionData, addDoc, updateDoc, deleteDoc, doc, query, where, getDocs, writeBatch } from '@angular/fire/firestore';
import { Observable, Subscription } from 'rxjs';
import { BrandService } from '../services/brand.service';

export interface SmrsRate {
  id?: string;
  name: string;
  rate: number; // Percentage
  kcegRate?: number;
  importKeys: string[]; // The values in "서버비 구분"
  importKey?: string; // Legacy support
  period?: string;
  order?: number;
  brand?: string;
}

export interface KeyGroup {
  key: string;
  ips: string[];
  expanded?: boolean;
}

@Component({
  selector: 'app-smrs-rate-popup',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './smrs-rate-popup.component.html',
  styleUrls: ['./smrs-rate-popup.component.css']
})
export class SmrsRatePopupComponent implements OnInit, OnChanges, OnDestroy {
  private firestore = inject(Firestore);
  private elementRef = inject(ElementRef);
  private brandService = inject(BrandService);
  @Input() availableKeys: KeyGroup[] = [];
  @Input() periods: string[] = [];
  @Input() isInline: boolean = false;
  rates$: Observable<SmrsRate[]>;
  rates: SmrsRate[] = [];
  private brandSubscription?: Subscription;
  
  isModalOpen = false;
  editingRate: SmrsRate | null = null;
  selectedPeriod: string = '';
  selectedBrandId: string = '';
  selectedBrandName: string = 'LFC';
  dbKeys: KeyGroup[] = [];
  private dbKeysCache = new Map<string, KeyGroup[]>();
  localRates: SmrsRate[] = [];
  deletedRateIds: Set<string> = new Set();
  draggedRate: SmrsRate | null = null;
  displayKeys: KeyGroup[] = [];
  private isDataLoaded = false;
  isCopySpecificMonthOpen: boolean = false;
  copySpecificPeriod: string = '';
  
  // Form model
  formData: Partial<SmrsRate> = {
    name: '',
    rate: 0,
    kcegRate: 0,
    importKeys: [],
    period: '',
    brand: ''
  };

  constructor() {
    const ratesCol = collection(this.firestore, 'smrs_rates');
    this.rates$ = collectionData(ratesCol, { idField: 'id' }) as Observable<any[]>;
  }

  ngOnInit() {
    this.brandSubscription = this.brandService.selectedBrand$.subscribe(brand => {
      const newBrandId = brand?.id || '';
      const newBrandName = brand?.name || 'LFC';
      if (this.selectedBrandId !== newBrandId) {
        this.selectedBrandId = newBrandId;
        this.selectedBrandName = newBrandName;
        if (this.isModalOpen) {
          this.cancelEdit();
          this.updateDisplayKeys();
        }
      }
    });

    this.rates$.subscribe(data => {
      // Handle legacy data where importKey might be a string instead of array
      this.rates = data.map(r => ({
        ...r,
        importKeys: r.importKeys || (r.importKey ? [r.importKey] : [])
      })).sort((a, b) => (a.order || 0) - (b.order || 0));

      if (!this.isDataLoaded) {
        this.isDataLoaded = true;
        if (this.isModalOpen) {
          this.localRates = JSON.parse(JSON.stringify(this.rates));
          this.updateDisplayKeys();
        }
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['availableKeys']) {
      this.updateDisplayKeys();
    }
  }

  ngOnDestroy() {
    if (this.brandSubscription) {
      this.brandSubscription.unsubscribe();
    }
  }

  get availableCopyPeriods(): string[] {
    return (this.periods || []).filter(p => p !== this.selectedPeriod);
  }

  open(period: string = '') {
    this.isModalOpen = true;
    this.selectedPeriod = period;
    this.isCopySpecificMonthOpen = false;
    this.copySpecificPeriod = '';
    // Create a deep copy for local editing
    this.localRates = JSON.parse(JSON.stringify(this.rates));
    this.deletedRateIds.clear();

    if (period) {
      this.fetchDbKeys(period);
    } else {
      this.dbKeys = [];
    }
    this.updateDisplayKeys();
  }

  close() {
    if (this.isInline) return;
    this.isModalOpen = false;
    this.editingRate = null;
    this.isCopySpecificMonthOpen = false;
    this.copySpecificPeriod = '';
  }

  async onPeriodChange() {
    this.isCopySpecificMonthOpen = false;
    this.copySpecificPeriod = '';
    if (this.selectedPeriod) {
      await this.fetchDbKeys(this.selectedPeriod);
    } else {
      this.dbKeys = [];
    }
    this.updateDisplayKeys();
  }

  async fetchDbKeys(period: string) {
    if (this.dbKeysCache.has(period)) {
      this.dbKeys = this.dbKeysCache.get(period)!;
      this.updateDisplayKeys();
      return;
    }

    try {
      const aggRef = collection(this.firestore, 'monthly_store_aggregations');
      const q = query(aggRef, where('period', '==', period));
      const snap = await getDocs(q);
      const keyMap = new Map<string, Set<string>>();

      snap.forEach(doc => {
        const d = doc.data();
        if (d['revenues']) {
          Object.keys(d['revenues']).forEach(k => {
            if (!keyMap.has(k)) keyMap.set(k, new Set());
          });
        }
        if (d['ips']) {
          Object.entries(d['ips']).forEach(([feeType, ipMap]: [string, any]) => {
            if (!keyMap.has(feeType)) keyMap.set(feeType, new Set());
            if (ipMap) {
              Object.keys(ipMap).forEach(ip => keyMap.get(feeType)!.add(ip));
            }
          });
        }
      });
      this.dbKeys = Array.from(keyMap.entries())
        .map(([key, ipsSet]) => ({ key, ips: Array.from(ipsSet).sort() }))
        .sort((a, b) => a.key.localeCompare(b.key));
      this.dbKeysCache.set(period, this.dbKeys);
      this.updateDisplayKeys();
    } catch (e) {
      console.error('Error fetching keys:', e);
    }
  }

  clearCache() {
    this.dbKeysCache.clear();
  }

  get filteredRates() {
    return this.localRates.filter(r => {
      const matchesPeriod = !this.selectedPeriod ? !r.period : r.period === this.selectedPeriod;
      const matchesBrand = r.brand === this.selectedBrandId;
      return matchesPeriod && matchesBrand;
    }).sort((a, b) => (a.order || 0) - (b.order || 0));
  }

  updateDisplayKeys() {
    const combined = new Map<string, Set<string>>();
    const rateIds = new Set(this.rates.map(r => r.id).filter(id => !!id));

    const merge = (groups: KeyGroup[]) => {
      groups.forEach(g => {
        if (rateIds.has(g.key)) return;
        if (!combined.has(g.key)) combined.set(g.key, new Set());
        g.ips.forEach(ip => combined.get(g.key)!.add(ip));
      });
    };

    merge(this.availableKeys);
    merge(this.dbKeys);

    this.displayKeys = Array.from(combined.entries())
      .map(([key, ipsSet]) => ({
        key,
        ips: Array.from(ipsSet).sort(),
        expanded: false
      }))
      .sort((a, b) => a.key.localeCompare(b.key));
  }

  isGroupSelected(group: KeyGroup): boolean {
    return (this.formData.importKeys || []).includes(group.key);
  }

  isGroupPartiallySelected(group: KeyGroup): boolean {
    if (this.isGroupSelected(group)) return false;
    const currentKeys = this.formData.importKeys || [];
    return group.ips.some(ip => currentKeys.includes(ip));
  }

  isIpSelected(group: KeyGroup, ip: string): boolean {
    if (this.isGroupSelected(group)) return true;
    return (this.formData.importKeys || []).includes(ip);
  }

  toggleGroup(group: KeyGroup) {
    const currentKeys = this.formData.importKeys || [];
    if (this.isGroupSelected(group)) {
      // Uncheck group: remove group key
      this.formData.importKeys = currentKeys.filter(k => k !== group.key);
    } else {
      // Check group: add group key, remove any individual IPs of this group to avoid double counting
      const newKeys = currentKeys.filter(k => !group.ips.includes(k));
      newKeys.push(group.key);
      this.formData.importKeys = newKeys;
    }
  }

  toggleIp(group: KeyGroup, ip: string) {
    let currentKeys = this.formData.importKeys || [];
    
    if (this.isGroupSelected(group)) {
      // Group is currently selected. User unchecks an IP.
      // We must remove Group Key, and add ALL OTHER IPs.
      currentKeys = currentKeys.filter(k => k !== group.key);
      const otherIps = group.ips.filter(i => i !== ip);
      currentKeys.push(...otherIps);
    } else {
      // Group not selected.
      if (currentKeys.includes(ip)) {
        // Uncheck IP
        currentKeys = currentKeys.filter(k => k !== ip);
      } else {
        // Check IP
        // Check if adding this IP makes all IPs selected
        const otherIps = group.ips.filter(i => i !== ip);
        const allOthersSelected = otherIps.every(i => currentKeys.includes(i));
        
        if (allOthersSelected) {
           // Switch to Group Key
           currentKeys = currentKeys.filter(k => !otherIps.includes(k));
           currentKeys.push(group.key);
        } else {
           currentKeys.push(ip);
        }
      }
    }
    this.formData.importKeys = currentKeys;
  }

  openAddModal() {
    this.editingRate = null;
    this.formData = { name: '', rate: 0, kcegRate: 0, importKeys: [], period: this.selectedPeriod, brand: this.selectedBrandId };
  }

  editRate(rate: SmrsRate) {
    this.editingRate = rate;
    // Ensure we work with a copy and importKeys is an array
    this.formData = { 
      ...rate,
      kcegRate: rate.kcegRate ?? 0,
      importKeys: rate.importKeys ? [...rate.importKeys] : (rate.importKey ? [rate.importKey] : []),
      period: rate.period || '',
      brand: rate.brand || ''
    };
  }

  cancelEdit() {
    this.editingRate = null;
    this.formData = { name: '', rate: 0, kcegRate: 0, importKeys: [], period: this.selectedPeriod, brand: this.selectedBrandId };
  }

  saveRate() {
    const isImportKeysMissing = this.selectedBrandName === 'LFC' && (!this.formData.importKeys || this.formData.importKeys.length === 0);
    if (!this.formData.name || this.formData.rate === undefined || this.formData.rate === null || this.formData.kcegRate === undefined || this.formData.kcegRate === null || isImportKeysMissing) {
      alert('Please fill in all fields');
      return;
    }

    try {
      // Clean up legacy field if it exists in the object
      const dataToSave: any = { ...this.formData };
      dataToSave.rate = Number(dataToSave.rate);
      dataToSave.kcegRate = Number(dataToSave.kcegRate);
      delete dataToSave.importKey;
      if (this.selectedPeriod) {
        dataToSave.period = this.selectedPeriod;
      } else {
        delete dataToSave.period;
      }
      dataToSave.brand = this.selectedBrandId;

      if (this.editingRate) {
        // Update existing in localRates
        const index = this.localRates.findIndex(r => r.id === this.editingRate!.id && r.name === this.editingRate!.name); 
        // Note: Using ID match if available, or reference match if we had references. 
        // Since we deep copied, references in editingRate (from filteredRates) point to localRates objects.
        // However, let's be safe and find by reference since editingRate comes from the view loop.
        
        // Actually, editingRate is set from the view loop, which iterates filteredRates, which comes from localRates.
        // So editingRate IS a reference to an object in localRates.
        Object.assign(this.editingRate, dataToSave);
      } else {
        // Add new
        const maxOrder = this.filteredRates.length > 0 
            ? Math.max(...this.filteredRates.map(r => r.order || 0)) 
            : 0;
        dataToSave.order = maxOrder + 1;
        dataToSave.id = 'temp_' + Date.now() + Math.random().toString(36).substring(2, 7);
        this.localRates.push(dataToSave as SmrsRate);
      }
      this.cancelEdit();
    } catch (e) {
      console.error('Error applying rate change:', e);
    }
  }

  copyFromPeriod(sourcePeriod: string) {
    if (!sourcePeriod || !this.selectedPeriod) return;

    const sourceRates = this.localRates.filter(r => r.period === sourcePeriod && r.brand === this.selectedBrandId);
    
    if (sourceRates.length === 0) {
      alert(`No ${this.selectedBrandName} rates found in ${sourcePeriod} to copy.`);
      return;
    }

    const currentRates = this.localRates.filter(r => r.period === this.selectedPeriod && r.brand === this.selectedBrandId);
    if (currentRates.length > 0) {
      if (!confirm(`Current period (${this.selectedPeriod}) already has ${this.selectedBrandName} rates. Do you want to copy rates from ${sourcePeriod}? (Duplicates will be skipped)`)) {
        return;
      }
    }

    let addedCount = 0;
    const currentMaxOrder = currentRates.length > 0 
        ? Math.max(...currentRates.map(r => r.order || 0)) 
        : 0;

    // Sort source rates to maintain relative order
    const sortedSourceRates = [...sourceRates].sort((a, b) => (a.order || 0) - (b.order || 0));

    sortedSourceRates.forEach((srcRate, index) => {
      const exists = this.localRates.some(r => r.period === this.selectedPeriod && r.brand === this.selectedBrandId && r.name === srcRate.name);
      
      if (!exists) {
        const newRate: SmrsRate = JSON.parse(JSON.stringify(srcRate));
        newRate.id = 'temp_' + Date.now() + Math.random().toString(36).substring(2, 7);
        newRate.period = this.selectedPeriod;
        newRate.brand = this.selectedBrandId;
        newRate.order = currentMaxOrder + 1 + index;
        
        this.localRates.push(newRate);
        addedCount++;
      }
    });

    if (addedCount > 0) {
      alert(`Copied ${addedCount} rates from ${sourcePeriod}.`);
    } else {
      alert(`All rates from ${sourcePeriod} already exist in current period.`);
    }
  }

  copyFromPreviousPeriod() {
    if (!this.selectedPeriod) return;

    const currentIndex = this.periods.indexOf(this.selectedPeriod);
    // If current is the last one (oldest), we can't go back further in this list
    if (currentIndex === -1 || currentIndex === this.periods.length - 1) {
      alert('No previous period available to copy from.');
      return;
    }

    const previousPeriod = this.periods[currentIndex + 1];
    this.copyFromPeriod(previousPeriod);
  }

  toggleCopySpecificMonth() {
    this.isCopySpecificMonthOpen = !this.isCopySpecificMonthOpen;
    this.copySpecificPeriod = '';
  }

  onSelectSpecificPeriod(period: string) {
    if (!period) return;
    this.copyFromPeriod(period);
    this.isCopySpecificMonthOpen = false;
    this.copySpecificPeriod = '';
  }

  deleteRate(rate: SmrsRate) {
    if (!confirm('Are you sure you want to delete this rate?')) return;
    if (rate.id && !rate.id.startsWith('temp_')) {
      this.deletedRateIds.add(rate.id);
    }
    this.localRates = this.localRates.filter(r => r !== rate);
  }

  async saveAllChanges() {
    try {
      const batch = writeBatch(this.firestore);

      // 1. Handle Deletions
      this.deletedRateIds.forEach(id => {
        batch.delete(doc(this.firestore, 'smrs_rates', id));
      });

      // 2. Handle Updates and Adds
      this.localRates.forEach(rate => {
        const dataToSave = { ...rate };
        if (rate.id && !rate.id.startsWith('temp_')) {
          const ref = doc(this.firestore, 'smrs_rates', rate.id);
          batch.update(ref, dataToSave);
        } else {
          const ref = doc(collection(this.firestore, 'smrs_rates'));
          delete dataToSave.id; // clear temp id before writing to DB
          batch.set(ref, dataToSave);
          rate.id = ref.id; // assign the real document id for future inline edits
        }
      });

      await batch.commit();
      if (this.isInline) {
        alert('Changes saved successfully.');
        this.deletedRateIds.clear();
      } else {
        this.close();
      }
    } catch (e) {
      console.error('Error saving changes:', e);
      alert('Failed to save changes to database.');
    }
  }

  onDragStart(event: DragEvent, rate: SmrsRate) {
    this.draggedRate = rate;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', rate.id || '');
    }
    // Add dragging class for visual feedback
    if (event.target instanceof HTMLElement) {
      event.target.classList.add('dragging');
    }
  }

  onDragEnd(event: DragEvent) {
    this.draggedRate = null;
    if (event.target instanceof HTMLElement) {
      event.target.classList.remove('dragging');
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault(); // Allow drop
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  }

  async onDrop(event: DragEvent, targetRate: SmrsRate) {
    event.preventDefault();
    if (!this.draggedRate || this.draggedRate.id === targetRate.id) return;

    const list = this.filteredRates;
    const fromIndex = list.findIndex(r => r.id === this.draggedRate!.id);
    const toIndex = list.findIndex(r => r.id === targetRate.id);

    if (fromIndex === -1 || toIndex === -1) return;
    
    const sorted = [...list];
    const [movedItem] = sorted.splice(fromIndex, 1);
    sorted.splice(toIndex, 0, movedItem);
    
    // Update order locally
    sorted.forEach((r, i) => {
      r.order = i;
    });
    
    // Trigger change detection for sorting if needed, though object mutation should be enough for the getter next time it's called
    
    this.draggedRate = null;
  }

  @HostListener('document:keydown.escape', ['$event'])
  onKeydownHandler(event: Event) {
    if (this.isModalOpen && !this.isInline) {
      this.close();
    }
  }
}
